/* istanbul ignore file */
import React, { useEffect } from 'react'
import classnames from 'classnames/bind'
import {
  APIProvider,
  Map as GoogleMap,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  ControlPosition,
  useMap,
} from '@vis.gl/react-google-maps'
import PropTypes from 'prop-types'
import { DEFAULT_ZOOM_LEVEL, US_CENTER_LAT_LNG } from '../../constants/map'
import Marker from '../marker'
import { getLatitude, getLongitude } from '../../util/mapUtils'
import { isMobile } from '../../util/osDetector'
import { useIsHovered } from '../searchPanel/hoverContext'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const GOOGLE_MAPS_LIBRARIES = ['places']

// zIndex applied to the hovered marker's AdvancedMarkerElement so its popup
// card (and dot) stack above every other marker. Inner CSS z-index can't do
// this: each AdvancedMarker is a separate stacking context ordered by Google
// (by latitude), so a lower marker's dot would otherwise cover the card.
const HOVERED_MARKER_Z_INDEX = 10000

/**
 * Bridges the @vis.gl map instance back out to the parent (markerMap), which
 * owns all the imperative map logic (fitBounds, bounds tracking, initial fit,
 * mobile init) against the raw google.maps.Map. Mirrors google-map-react's
 * onGoogleApiLoaded({ map }).
 */
const MapReadyBridge = ({ onGoogleMapsLoad }) => {
  const map = useMap()
  useEffect(() => {
    if (map) onGoogleMapsLoad({ map })
  }, [map, onGoogleMapsLoad])
  return null
}

MapReadyBridge.propTypes = {
  onGoogleMapsLoad: PropTypes.func.isRequired,
}

/**
 * Single marker. Reads hover state so it can raise its AdvancedMarker zIndex
 * above sibling markers while hovered (fixes the popup card being covered by
 * other dots).
 */
const MapMarker = ({
  listing,
  index,
  onMarkerClick,
  bounds,
  map,
  featureFlags,
}) => {
  const lat = getLatitude(listing)
  const lng = getLongitude(listing)
  const isHovered = useIsHovered(listing.listing_id)
  if (!lat || !lng) return null
  // Only lift zIndex on desktop, where the popup card renders as marker content
  // and must stack above other markers. On mobile the card renders at the
  // bottom of the screen (not as a marker popup); selecting a pin sets hover
  // (markerMap opens the mobile card via setHover), so lifting zIndex there
  // just churns the AdvancedMarker prop on every tap -> flicker/stutter.
  const zIndex = !isMobile() && isHovered ? HOVERED_MARKER_Z_INDEX : undefined
  return (
    <AdvancedMarker
      position={{ lat, lng }}
      anchorPoint={AdvancedMarkerAnchorPoint.TOP_LEFT}
      zIndex={zIndex}
      // NOTE: intentionally NOT `clickable`. We render our own focusable <a>
      // inside the marker for navigation/click. Enabling gmp clickability makes
      // AdvancedMarkerElement set up focus delegation over the focusable child,
      // which triggers Google's "Focusable child elements are not supported"
      // warning and a recursive capture-phase dispatch (RangeError: Maximum
      // call stack size exceeded), especially on right-click/contextmenu.
    >
      <Marker
        index={index}
        location={{ lat, lng }}
        onMarkerClick={onMarkerClick}
        listing={listing}
        map={map}
        bounds={bounds}
        featureFlags={featureFlags}
      />
    </AdvancedMarker>
  )
}

MapMarker.propTypes = {
  listing: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  bounds: PropTypes.object,
  map: PropTypes.object,
  featureFlags: PropTypes.object,
}

/**
 * Renders markers only once the map/API is ready (useMap() returns the live
 * instance). Guards against Marker's `new window.google.maps.Size(...)` running
 * before the Maps JS API has loaded (google-map-react gated on googleMapsLoaded).
 */
const MapMarkers = ({
  markers,
  assetsLoaded,
  onMarkerClick,
  bounds,
  map,
  featureFlags,
}) => {
  const gmap = useMap()
  if (!assetsLoaded || !gmap) return null
  return markers.map((listing, i) => (
    <MapMarker
      key={`marker_${i}`}
      listing={listing}
      index={i}
      onMarkerClick={onMarkerClick}
      bounds={bounds}
      map={map || gmap}
      featureFlags={featureFlags}
    />
  ))
}

MapMarkers.propTypes = {
  markers: PropTypes.array.isRequired,
  assetsLoaded: PropTypes.bool.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  bounds: PropTypes.object,
  map: PropTypes.object,
  featureFlags: PropTypes.object,
}

const Map = ({
  markers,
  onMarkerClick,
  onBoundsChanged,
  bounds,
  assetsLoaded,
  showMap,
  googleMapsApiKey,
  mapId,
  onGoogleMapsLoad,
  map,
  onMapClick,
  featureFlags,
}) => (
  <div className={cx('map-container', { 'hide-map': !showMap })}>
    <APIProvider
      apiKey={googleMapsApiKey}
      libraries={GOOGLE_MAPS_LIBRARIES}
      language='en'
      version='weekly'
    >
      <GoogleMap
        mapId={mapId}
        defaultCenter={US_CENTER_LAT_LNG}
        defaultZoom={DEFAULT_ZOOM_LEVEL}
        gestureHandling='greedy'
        clickableIcons={false}
        fullscreenControl={false}
        streetViewControl={false}
        mapTypeControl
        mapTypeControlOptions={{ position: ControlPosition.TOP_RIGHT }}
        zoomControl
        zoomControlOptions={{ position: ControlPosition.RIGHT_BOTTOM }}
        onIdle={onBoundsChanged}
        onClick={onMapClick}
        style={{ width: '100%', height: '100%' }}
      >
        <MapReadyBridge onGoogleMapsLoad={onGoogleMapsLoad} />
        <MapMarkers
          markers={markers}
          assetsLoaded={assetsLoaded}
          onMarkerClick={onMarkerClick}
          bounds={bounds}
          map={map}
          featureFlags={featureFlags}
        />
      </GoogleMap>
    </APIProvider>
  </div>
)

Map.propTypes = {
  markers: PropTypes.array.isRequired,
  onMarkerClick: PropTypes.func.isRequired,
  onBoundsChanged: PropTypes.func.isRequired,
  bounds: PropTypes.object,
  assetsLoaded: PropTypes.bool.isRequired,
  showMap: PropTypes.bool.isRequired,
  googleMapsApiKey: PropTypes.string,
  mapId: PropTypes.string,
  onGoogleMapsLoad: PropTypes.func,
  map: PropTypes.object,
  onMapClick: PropTypes.func.isRequired,
  featureFlags: PropTypes.object.isRequired,
}

export default Map
